import { Link } from "react-router-dom";
import type { ReactNode } from "react";

// ---------------------------------------------------------------------------
// Small local presentational subcomponents
// ---------------------------------------------------------------------------

type FeatureCardProps = {
  icon: ReactNode;
  title: string;
  description: string;
};

function FeatureCard({ icon, title, description }: FeatureCardProps) {
  return (
    <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-md">
      <div
        aria-hidden="true"
        className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-2xl text-brand"
      >
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{description}</p>
    </div>
  );
}

type StepProps = {
  step: number;
  title: string;
  description: string;
};

function Step({ step, title, description }: StepProps) {
  return (
    <div className="flex flex-col rounded-2xl bg-white p-6 shadow-sm">
      <div
        aria-hidden="true"
        className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-brand text-base font-bold text-white"
      >
        {step}
      </div>
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{description}</p>
    </div>
  );
}

type IndustryCardProps = {
  title: string;
  description: string;
};

function IndustryCard({ title, description }: IndustryCardProps) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md">
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{description}</p>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand"
    >
      <path
        fillRule="evenodd"
        d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.5 7.6a1 1 0 0 1-1.424.006l-3.5-3.5a1 1 0 1 1 1.414-1.414l2.787 2.787 6.79-6.881a1 1 0 0 1 1.427-.012Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

type PriceFeature = { label: string; heading?: boolean };

type PriceCardProps = {
  name: string;
  tagline: string;
  features: PriceFeature[];
  highlighted?: boolean;
  cta: ReactNode;
};

function PriceCard({ name, tagline, features, highlighted, cta }: PriceCardProps) {
  return (
    <div
      className={[
        "relative flex flex-col rounded-2xl border bg-white p-6 shadow-sm transition",
        highlighted
          ? "border-brand shadow-lg ring-1 ring-brand/30 lg:scale-[1.03]"
          : "border-slate-200 hover:shadow-md",
      ].join(" ")}
    >
      {highlighted && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
          Most popular
        </span>
      )}
      <h3 className="text-xl font-bold text-slate-900">{name}</h3>
      <p className="mt-1 text-sm text-slate-500">{tagline}</p>
      <ul className="mt-6 flex-1 space-y-3">
        {features.map((f, i) =>
          f.heading ? (
            <li key={i} className="pt-1 text-sm font-semibold text-slate-900">
              {f.label}
            </li>
          ) : (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
              <CheckIcon />
              <span>{f.label}</span>
            </li>
          )
        )}
      </ul>
      <div className="mt-8">{cta}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function LandingPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Top nav */}
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
        <nav className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" className="text-xl font-bold tracking-tight text-slate-900">
            Reliadata
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <Link
              to="/login"
              className="text-sm font-medium text-slate-600 transition hover:text-slate-900"
            >
              Sign in
            </Link>
            <Link
              to="/signup"
              className="rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-dark"
            >
              Get started
            </Link>
          </div>
        </nav>
      </header>

      <main>
        {/* Hero */}
        <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
            <div className="max-w-3xl">
              <h1 className="text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                Cloud communications for{" "}
                <span className="text-brand">voice, messaging &amp; contact centers</span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">
                Launch automated voice campaigns, route calls intelligently across carriers, and
                manage everything from one control panel.
              </p>
              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/apply"
                  className="inline-flex items-center justify-center rounded-full bg-brand px-7 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-brand-dark"
                >
                  Apply for a trial
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center rounded-full border border-slate-500 px-7 py-3 text-base font-semibold text-white transition hover:border-slate-300 hover:bg-white/10"
                >
                  Sign in
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="bg-white py-20 sm:py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Everything you need to communicate at scale
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                A complete toolkit for voice, routing, and customer engagement.
              </p>
            </div>
            <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <FeatureCard
                icon={<span role="img" aria-label="">📊</span>}
                title="Real-Time Analytics"
                description="Track communication activity across the platform with real-time dashboards and detailed reporting, including call metrics, delivery performance, and system usage."
              />
              <FeatureCard
                icon={<span role="img" aria-label="">📞</span>}
                title="DID Numbers"
                description="Provision local and toll-free numbers nationwide for inbound and outbound communications. Manage numbers and routing directly from the control panel."
              />
              <FeatureCard
                icon={<span role="img" aria-label="">🔀</span>}
                title="Intelligent Carrier Routing"
                description="Optimize call delivery using multi-carrier routing and automatic failover to maintain reliability and voice quality."
              />
              <FeatureCard
                icon={<span role="img" aria-label="">🔌</span>}
                title="CRM & API Integration"
                description="Connect Reliadata with CRM platforms such as Odoo, SuiteCRM, or custom systems through REST APIs. Automatically synchronize communication activity and customer records."
              />
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-gradient-to-b from-slate-50 to-sky-50 py-20 sm:py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Quick Platform Setup
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Get up and running in four straightforward steps.
              </p>
            </div>
            <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <Step
                step={1}
                title="Create an Account"
                description="Register your organization and configure your communication workspace."
              />
              <Step
                step={2}
                title="Connect Contacts or CRM"
                description="Securely import contacts or synchronize with your CRM system."
              />
              <Step
                step={3}
                title="Configure Workflows"
                description="Create automated voice workflows, IVR menus, and routing rules."
              />
              <Step
                step={4}
                title="Monitor Activity"
                description="Track communication performance and system metrics in real time."
              />
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="bg-white py-20 sm:py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Transparent Usage-Based Pricing
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Start small and scale as you grow. Pay for what you use.
              </p>
            </div>
            <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-start">
              <PriceCard
                name="Starter"
                tagline="For small teams and organizations"
                features={[
                  { label: "Automated voice notifications" },
                  { label: "IVR call flows" },
                  { label: "Up to 10 concurrent channels" },
                  { label: "DID numbers ($1.50/month)" },
                  { label: "Real-time analytics dashboard" },
                  { label: "CSV or CRM contact integration" },
                  { label: "Email support" },
                ]}
                cta={
                  <Link
                    to="/signup"
                    className="block w-full rounded-full border border-brand px-5 py-3 text-center text-sm font-semibold text-brand transition hover:bg-brand hover:text-white"
                  >
                    Get started
                  </Link>
                }
              />
              <PriceCard
                name="Professional"
                tagline="For growing teams"
                highlighted
                features={[
                  { label: "Everything in Starter, plus:", heading: true },
                  { label: "Expanded concurrency (up to 50 channels)" },
                  { label: "CRM integrations" },
                  { label: "Agent dashboard" },
                  { label: "Priority support" },
                ]}
                cta={
                  <Link
                    to="/signup"
                    className="block w-full rounded-full bg-brand px-5 py-3 text-center text-sm font-semibold text-white shadow-sm transition hover:bg-brand-dark"
                  >
                    Get started
                  </Link>
                }
              />
              <PriceCard
                name="Enterprise"
                tagline="For high-volume organizations"
                features={[
                  { label: "Custom pricing available", heading: true },
                  { label: "High-capacity infrastructure" },
                  { label: "Dedicated SIP connectivity" },
                  { label: "Custom number management" },
                  { label: "Bulk DID pricing" },
                  { label: "API access" },
                  { label: "White-label options" },
                  { label: "Dedicated account manager" },
                ]}
                cta={
                  <a
                    href="mailto:sales@reliadata.io"
                    className="block w-full rounded-full border border-slate-300 px-5 py-3 text-center text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                  >
                    Contact Sales
                  </a>
                }
              />
            </div>
          </div>
        </section>

        {/* Industries */}
        <section className="bg-slate-50 py-20 sm:py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Designed for Many Industries
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Flexible workflows that adapt to how your organization communicates.
              </p>
            </div>
            <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <IndustryCard
                title="Healthcare"
                description="Appointment reminders, prescription notifications, and patient communication workflows."
              />
              <IndustryCard
                title="Customer Support"
                description="Inbound call routing, IVR automation, and agent dashboards for service teams."
              />
              <IndustryCard
                title="Logistics & Delivery"
                description="Delivery notifications, driver communication, and automated customer updates."
              />
              <IndustryCard
                title="Public Notifications"
                description="Municipal or organizational alerts for important announcements and updates."
              />
              <IndustryCard
                title="Financial Services"
                description="Account alerts, payment reminders, and customer service communications."
              />
              <IndustryCard
                title="Education"
                description="Campus alerts, enrollment notifications, and student communications."
              />
            </div>
          </div>
        </section>

        {/* Footer CTA band */}
        <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <div className="mx-auto max-w-6xl px-4 py-16 text-center sm:px-6">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Ready to get started?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-slate-300">
              Create your account and launch your first workflow in minutes.
            </p>
            <div className="mt-8">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center rounded-full bg-brand px-8 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-brand-dark"
              >
                Create your account
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8 text-center text-sm text-slate-500 sm:px-6">
          © Reliadata
        </div>
      </footer>
    </div>
  );
}
